import React, { useState } from 'react'
import {
  Brain, ChevronRight, CheckCircle, XCircle,
  RotateCcw, MessageCircle, Target,
  AlertTriangle, BookOpen, Sparkles, ChevronDown, ChevronUp
} from 'lucide-react'
import { useUnitProgress } from '../store/progressStore'
import {
  computePredictedFragments, FRAGMENT_TAG_CONFIG,
  type PredictedFragment
} from '../store/predictedFragments'

const UNIT_ID = 'G1-3'

interface TutorScenario {
  id: string
  label: string
  emoji: string
  category: string
  learnStepId: string
  question: string
  correctAnswer: string
  wrongAnswer: string
  misconceptionCode: string
  note: string
  analysis: {
    diagnosis: string
    rootCause: string
    correction: string
    example: string
  }
  followUps: Array<{ q: string; a: string }>
}

const DEMO_SCENARIOS: TutorScenario[] = [
  {
    id: 'S1',
    label: '시나리오 1',
    emoji: '🏛️',
    category: '다면체 구조',
    learnStepId: 'GC01',
    question: '사각뿔의 면·꼭짓점·모서리의 수를 구하시오.',
    correctAnswer: '면=5, 꼭짓점=5, 모서리=8',
    wrongAnswer: '면=6, 꼭짓점=8, 모서리=12 (사각기둥과 혼동)',
    misconceptionCode: 'GP01',
    note: 'n각뿔과 n각기둥의 공식을 혼동함',
    analysis: {
      diagnosis: '학생이 "면=6, 꼭짓점=8, 모서리=12"라고 답했습니다. 이는 사각기둥의 값으로, 각기둥과 각뿔 공식을 혼동했습니다.',
      rootCause: '각기둥과 각뿔은 공식이 다릅니다!\n• 각기둥: 면=n+2, 꼭짓점=2n, 모서리=3n\n• 각뿔: 면=n+1, 꼭짓점=n+1, 모서리=2n\n기둥은 밑면이 2개, 뿔은 밑면이 1개+꼭짓점 1개!',
      correction: '사각뿔(n=4):\n① 면의 수: n+1 = 4+1 = 5개 (밑면1+삼각형옆면4)\n② 꼭짓점의 수: n+1 = 4+1 = 5개 (밑면4+꼭짓점1)\n③ 모서리의 수: 2n = 2×4 = 8개\n오일러: 5+5-8=2 ✓',
      example: '삼각뿔: 면4, 꼭짓점4, 모서리6 → 4+4-6=2 ✓\n사각뿔: 면5, 꼭짓점5, 모서리8 → 5+5-8=2 ✓\n오각뿔: 면6, 꼭짓점6, 모서리10 → 6+6-10=2 ✓',
    },
    followUps: [
      { q: '육각기둥의 면·꼭짓점·모서리의 수는?', a: '면=8, 꼭짓점=12, 모서리=18. 오일러: 8+12-18=2 ✓' },
      { q: '오일러 공식 F+V-E=2는 모든 다면체에서 성립하나요?', a: '네! 모든 볼록 다면체에서 F+V-E=2가 성립합니다. 이는 위상수학의 기본 정리입니다.' },
    ],
  },
  {
    id: 'S2',
    label: '시나리오 2',
    emoji: '🔄',
    category: '회전체 단면',
    learnStepId: 'GC02',
    question: '원뿔을 회전축을 포함하는 평면으로 자를 때 단면의 모양은?',
    correctAnswer: '이등변삼각형',
    wrongAnswer: '원',
    misconceptionCode: 'GP02',
    note: '회전축에 평행한 단면과 수직한 단면을 혼동',
    analysis: {
      diagnosis: '학생이 "원"이라고 답했습니다. 회전축에 수직한 단면과 평행한 단면을 혼동했습니다.',
      rootCause: '단면의 방향에 따라 모양이 달라집니다!\n• 회전축에 수직한 단면: 원\n• 회전축에 평행한(포함하는) 단면: 이등변삼각형\n원뿔을 세워서 위에서 자르면 원, 옆으로 반 가르면 이등변삼각형!',
      correction: '원뿔의 단면:\n① 회전축에 수직: 원 (위에서 자름)\n② 회전축을 포함: 이등변삼각형 (옆으로 자름)\n   → 두 모선이 같으므로 이등변삼각형!\n\n원기둥의 단면:\n① 회전축에 수직: 원\n② 회전축에 평행: 직사각형',
      example: '구의 단면: 어떤 방향이든 항상 원!\n원뿔 수직단면: 원 (크기는 높이에 따라 다름)\n원뿔 평행단면: 이등변삼각형 (항상!)',
    },
    followUps: [
      { q: '구를 어떤 방향으로 잘라도 단면이 원인 이유는?', a: '구는 중심에서 표면까지의 거리(반지름)가 모두 같기 때문입니다. 어떤 방향으로 자르든 단면은 원이 됩니다.' },
      { q: '원기둥을 회전축에 45° 기울어진 평면으로 자르면?', a: '타원(ellipse) 모양이 됩니다. 수직=원, 평행=직사각형, 기울어짐=타원!' },
    ],
  },
  {
    id: 'S3',
    label: '시나리오 3',
    emoji: '📦',
    category: '원뿔 겉넓이',
    learnStepId: 'GC03',
    question: '반지름 3cm, 높이 4cm인 원뿔의 겉넓이는? (모선 l=5cm)',
    correctAnswer: 'πr²+πrl = 9π+15π = 24π cm²',
    wrongAnswer: 'πr²+2πrh = 9π+24π = 33π cm² (원기둥 공식 사용)',
    misconceptionCode: 'GP03',
    note: '원뿔 겉넓이에서 원기둥 옆면 공식(2πrh)을 사용',
    analysis: {
      diagnosis: '학생이 "9π+24π=33π"라고 답했습니다. 원뿔 옆면에 원기둥 공식(2πrh)을 사용했습니다.',
      rootCause: '원뿔 옆면은 부채꼴이지 직사각형이 아닙니다!\n• 원기둥 옆면: 직사각형 → 2πrh\n• 원뿔 옆면: 부채꼴 → πrl (l=모선)\n원뿔은 펼치면 부채꼴이 됩니다!',
      correction: '원뿔 겉넓이 = 밑면(원) + 옆면(부채꼴)\n① 밑면: πr² = π×9 = 9π cm²\n② 옆면: πrl = π×3×5 = 15π cm²\n   (l=모선=√(r²+h²)=√(9+16)=√25=5)\n③ 겉넓이: 9π+15π = 24π cm²',
      example: '원기둥 겉넓이: 2πr²+2πrh (양쪽원+직사각형)\n원뿔 겉넓이: πr²+πrl (원1개+부채꼴)\n구 겉넓이: 4πr² (면 없음, 곡면만)',
    },
    followUps: [
      { q: '원뿔에서 모선(l)은 어떻게 구하나요?', a: 'l=√(r²+h²) (피타고라스 정리). 반지름r과 높이h로 이루어진 직각삼각형의 빗변이 모선입니다.' },
      { q: '반지름 6cm, 모선 10cm인 원뿔의 겉넓이는?', a: 'πr²+πrl = π×36+π×6×10 = 36π+60π = 96π cm²' },
    ],
  },
  {
    id: 'S4',
    label: '시나리오 4',
    emoji: '🚰',
    category: '원뿔 부피 (1/3 누락)',
    learnStepId: 'GC04',
    question: '반지름 3cm, 높이 4cm인 원뿔의 부피는?',
    correctAnswer: '(1/3)πr²h = (1/3)×π×9×4 = 12π cm³',
    wrongAnswer: 'πr²h = π×9×4 = 36π cm³ ((1/3) 누락)',
    misconceptionCode: 'GP04',
    note: '원뿔 부피에서 (1/3)을 빠뜨림',
    analysis: {
      diagnosis: '학생이 "36π cm³"라고 답했습니다. 원뿔 부피 공식에서 (1/3)을 빠뜨리고 원기둥 공식을 사용했습니다.',
      rootCause: '원뿔·각뿔 부피는 반드시 (1/3)을 곱해야 합니다!\n• 원기둥 부피: πr²h\n• 원뿔 부피: (1/3)πr²h\n같은 밑면·높이에서 원뿔=원기둥×(1/3)입니다!',
      correction: '원뿔 부피 = (1/3) × 밑면 넓이 × 높이\n① 밑면 넓이: πr² = 9π cm²\n② 부피: (1/3)×9π×4 = (1/3)×36π = 12π cm³\n\n검증: 원기둥(36π)의 1/3 = 12π ✓',
      example: '각기둥 부피: Sh (기둥 전체)\n각뿔 부피: (1/3)Sh (기둥의 1/3)\n원기둥 부피: πr²h\n원뿔 부피: (1/3)πr²h\n구 부피: (4/3)πr³',
    },
    followUps: [
      { q: '구 부피 공식 (4/3)πr³은 어떻게 유도되나요?', a: '구를 아주 많은 작은 원뿔들의 합으로 생각하면: 부피≈Σ(1/3)×표면원소×r = (1/3)×4πr²×r = (4/3)πr³ 입니다.' },
      { q: '반지름 6cm인 구의 부피는?', a: '(4/3)π×6³=(4/3)π×216=288π cm³' },
    ],
  },
  {
    id: 'S5',
    label: '시나리오 5',
    emoji: '🌍',
    category: '구 겉넓이·부피 활용',
    learnStepId: 'GC04',
    question: '반지름이 2배가 되면 구의 부피는 몇 배가 되는가?',
    correctAnswer: '8배 (r³이므로 2³=8배)',
    wrongAnswer: '2배 또는 4배',
    misconceptionCode: 'GP04',
    note: '부피는 r³에 비례함을 이해하지 못함',
    analysis: {
      diagnosis: '학생이 "2배" 또는 "4배"라고 답했습니다. 부피가 r³에 비례한다는 사실을 이해하지 못했습니다.',
      rootCause: '크기별 비례 관계:\n• 반지름 2배 → 겉넓이 4배 (r²이므로 2²=4배)\n• 반지름 2배 → 부피 8배 (r³이므로 2³=8배)\n넓이는 제곱, 부피는 세제곱!',
      correction: '구 부피 = (4/3)πr³\nr이 2배→ (4/3)π(2r)³=(4/3)π×8r³\n= 8×(4/3)πr³ → 8배!\n\n구 겉넓이 = 4πr²\nr이 2배→ 4π(2r)²=4π×4r²=4배!',
      example: 'r=1: 부피=(4/3)π\nr=2: 부피=(4/3)×8π=32π/3\n비율: 32/3÷4/3=8 → 8배 ✓\n겉넓이: 4π → 16π → 4배 ✓',
    },
    followUps: [
      { q: '반지름이 3배가 되면 구의 부피는 몇 배가 되나요?', a: '3³=27배. 부피는 반지름의 세제곱에 비례합니다!' },
      { q: '지구 반지름이 달 반지름의 약 3.7배라면 지구 부피는 달의 몇 배?', a: '3.7³≈50.6배. 지구의 부피는 달의 약 50배입니다!' },
    ],
  },
]

// ── PredictedFragmentPanel ────────────────────────────────────────────────────
function PredictedFragmentPanel({ fragments }: { fragments: PredictedFragment[] }) {
  const [open, setOpen] = useState(true)
  if (fragments.length === 0) return null
  return (
    <div className="mb-4 card border border-amber-200 bg-amber-50">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold text-amber-700 flex items-center gap-1">
          <Brain size={12} /> AI 예상 취약 개념 ({fragments.length}개)
        </p>
        <button onClick={() => setOpen(o => !o)} className="text-amber-400 hover:text-amber-600">
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>
      {open && (
        <div className="mt-2 space-y-1.5">
          {fragments.slice(0, 3).map(f => {
            const cfg = FRAGMENT_TAG_CONFIG[f.tag] ?? FRAGMENT_TAG_CONFIG['review']
            return (
              <div key={f.fragment.id} className={`flex items-center gap-2 p-2 rounded-lg border ${cfg.color}`}>
                <span>{f.fragment.emoji}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold truncate">{f.fragment.title}</p>
                  <p className="text-xs text-slate-500 truncate">{f.fragment.fixTip.slice(0, 40)}...</p>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── 메인 컴포넌트 ────────────────────────────────────────────────────────────
export default function G1_3TutorPage() {
  const [selectedId, setSelectedId] = useState(DEMO_SCENARIOS[0].id)
  const [step, setStep] = useState<'question' | 'wrong' | 'analysis' | 'correction' | 'followup'>('question')
  const [followUpIdx, setFollowUpIdx] = useState(0)
  const [showFollowUpAnswer, setShowFollowUpAnswer] = useState(false)

  const unitProgress = useUnitProgress(UNIT_ID)
  const predictedFragments = computePredictedFragments(unitProgress, 3)

  const sc = DEMO_SCENARIOS.find(s => s.id === selectedId)!

  const reset = (id: string) => {
    setSelectedId(id)
    setStep('question')
    setFollowUpIdx(0)
    setShowFollowUpAnswer(false)
  }

  const categoryColor: Record<string, string> = {
    '다면체 구조': 'bg-sky-100 text-sky-700',
    '회전체 단면': 'bg-violet-100 text-violet-700',
    '원뿔 겉넓이': 'bg-orange-100 text-orange-700',
    '원뿔 부피 (1/3 누락)': 'bg-red-100 text-red-700',
    '구 겉넓이·부피 활용': 'bg-teal-100 text-teal-700',
  }

  return (
    <div className="p-4 max-w-3xl mx-auto">
      <PredictedFragmentPanel fragments={predictedFragments} />

      {/* 헤더 */}
      <div className="mb-5 rounded-2xl bg-gradient-to-r from-sky-600 to-blue-600 text-white p-5">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <Brain size={22} className="text-white" />
          </div>
          <div>
            <h2 className="font-bold text-lg">🧊 입체도형 AI 튜터</h2>
            <p className="text-xs text-sky-100">오개념을 진단하고 올바른 이해로 안내합니다</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 mt-3">
          {[
            { label: '다루는 오개념', value: `${DEMO_SCENARIOS.length}가지` },
            { label: '학습 단계', value: 'GC01~GC04' },
            { label: '추천 시간', value: '20~30분' },
          ].map(item => (
            <div key={item.label} className="bg-white/15 rounded-xl p-2 text-center">
              <p className="text-xs text-sky-100">{item.label}</p>
              <p className="text-sm font-bold">{item.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* 시나리오 선택 */}
      <div className="mb-5">
        <p className="text-xs font-bold text-slate-500 mb-2">📋 시나리오 선택</p>
        <div className="space-y-2">
          {DEMO_SCENARIOS.map(s => (
            <button
              key={s.id}
              onClick={() => reset(s.id)}
              className={`w-full text-left rounded-xl border-2 px-4 py-3 transition-all ${
                selectedId === s.id
                  ? 'border-sky-400 bg-sky-50 shadow-sm'
                  : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">{s.emoji}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-slate-800">{s.label}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${categoryColor[s.category] ?? 'bg-slate-100 text-slate-600'}`}>
                      {s.category}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5 truncate">{s.question}</p>
                </div>
                <ChevronRight size={14} className="text-slate-300 flex-shrink-0" />
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* 튜터 인터페이스 */}
      <div className="card border-2 border-sky-100">
        {/* 단계 표시 */}
        <div className="flex gap-1 mb-5 overflow-x-auto">
          {(['question', 'wrong', 'analysis', 'correction', 'followup'] as const).map((s, i) => {
            const labels = ['문제', '오답', '진단', '교정', '심화']
            const icons = ['❓', '⚠️', '🔍', '✅', '🚀']
            const active = step === s
            const passed = ['question', 'wrong', 'analysis', 'correction', 'followup'].indexOf(step) > i
            return (
              <button
                key={s}
                onClick={() => setStep(s)}
                className={`flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                  active ? 'bg-sky-600 text-white' : passed ? 'bg-sky-100 text-sky-700' : 'bg-slate-100 text-slate-400'
                }`}
              >
                <span>{icons[i]}</span>
                <span>{labels[i]}</span>
              </button>
            )
          })}
        </div>

        {/* 문제 단계 */}
        {step === 'question' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">{sc.emoji}</span>
              <div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${categoryColor[sc.category] ?? 'bg-slate-100 text-slate-600'}`}>
                  {sc.category}
                </span>
                <p className="text-xs text-slate-400 mt-0.5">{sc.note}</p>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-xs font-bold text-blue-600 mb-2 flex items-center gap-1">
                <MessageCircle size={12} /> 문제
              </p>
              <p className="text-sm font-semibold text-slate-800">{sc.question}</p>
            </div>
            <p className="text-xs text-slate-500 text-center">먼저 스스로 풀어보세요! 준비되면 아래 버튼을 눌러주세요.</p>
            <button
              onClick={() => setStep('wrong')}
              className="w-full py-3 bg-sky-600 hover:bg-sky-700 text-white rounded-xl font-bold text-sm transition-colors flex items-center justify-center gap-2"
            >
              <ChevronRight size={16} /> 오답 예시 보기
            </button>
          </div>
        )}

        {/* 오답 단계 */}
        {step === 'wrong' && (
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-xs font-bold text-red-600 mb-2 flex items-center gap-1">
                <XCircle size={12} /> 흔한 오답
              </p>
              <p className="text-sm font-mono text-red-800">{sc.wrongAnswer}</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <p className="text-xs font-bold text-emerald-600 mb-2 flex items-center gap-1">
                <CheckCircle size={12} /> 올바른 답
              </p>
              <p className="text-sm font-mono text-emerald-800">{sc.correctAnswer}</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <p className="text-xs text-amber-700">
                <span className="font-bold">오개념 코드: </span>{sc.misconceptionCode} — {sc.note}
              </p>
            </div>
            <button
              onClick={() => setStep('analysis')}
              className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-bold text-sm transition-colors flex items-center justify-center gap-2"
            >
              <Target size={16} /> AI 진단 보기
            </button>
          </div>
        )}

        {/* 진단 단계 */}
        {step === 'analysis' && (
          <div className="space-y-4">
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
              <p className="text-xs font-bold text-slate-600 mb-2 flex items-center gap-1">
                <Brain size={12} /> AI 진단
              </p>
              <p className="text-sm text-slate-700 leading-relaxed">{sc.analysis.diagnosis}</p>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
              <p className="text-xs font-bold text-orange-600 mb-2 flex items-center gap-1">
                <AlertTriangle size={12} /> 오개념 원인
              </p>
              <p className="text-sm text-orange-800 leading-relaxed whitespace-pre-line">{sc.analysis.rootCause}</p>
            </div>
            <button
              onClick={() => setStep('correction')}
              className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-sm transition-colors flex items-center justify-center gap-2"
            >
              <CheckCircle size={16} /> 올바른 개념 배우기
            </button>
          </div>
        )}

        {/* 교정 단계 */}
        {step === 'correction' && (
          <div className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <p className="text-xs font-bold text-emerald-600 mb-3 flex items-center gap-1">
                <CheckCircle size={12} /> 올바른 풀이
              </p>
              <p className="text-sm text-emerald-800 leading-relaxed whitespace-pre-line font-mono">{sc.analysis.correction}</p>
            </div>
            <div className="bg-sky-50 border border-sky-200 rounded-xl p-4">
              <p className="text-xs font-bold text-sky-600 mb-2 flex items-center gap-1">
                <BookOpen size={12} /> 추가 예시
              </p>
              <p className="text-sm text-sky-800 font-mono whitespace-pre-line">{sc.analysis.example}</p>
            </div>
            <button
              onClick={() => { setStep('followup'); setFollowUpIdx(0); setShowFollowUpAnswer(false) }}
              className="w-full py-3 bg-violet-600 hover:bg-violet-700 text-white rounded-xl font-bold text-sm transition-colors flex items-center justify-center gap-2"
            >
              <Sparkles size={16} /> 심화 문제 풀기
            </button>
          </div>
        )}

        {/* 심화 단계 */}
        {step === 'followup' && (
          <div className="space-y-4">
            <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
              <p className="text-xs font-bold text-violet-600 mb-2 flex items-center gap-1">
                <Sparkles size={12} /> 심화 문제 {followUpIdx + 1}/{sc.followUps.length}
              </p>
              <p className="text-sm font-semibold text-slate-800">{sc.followUps[followUpIdx].q}</p>
            </div>
            {!showFollowUpAnswer ? (
              <button
                onClick={() => setShowFollowUpAnswer(true)}
                className="w-full py-2.5 bg-violet-100 hover:bg-violet-200 text-violet-700 rounded-xl font-semibold text-sm transition-colors"
              >
                정답 확인
              </button>
            ) : (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                <p className="text-xs font-bold text-emerald-600 mb-1">✅ 정답</p>
                <p className="text-sm text-emerald-800 font-mono whitespace-pre-line">{sc.followUps[followUpIdx].a}</p>
              </div>
            )}
            <div className="flex gap-2">
              {followUpIdx < sc.followUps.length - 1 ? (
                <button
                  onClick={() => { setFollowUpIdx(i => i + 1); setShowFollowUpAnswer(false) }}
                  className="flex-1 py-2.5 bg-sky-600 hover:bg-sky-700 text-white rounded-xl font-bold text-sm transition-colors"
                >
                  다음 문제 →
                </button>
              ) : (
                <button
                  onClick={() => reset(sc.id)}
                  className="flex-1 py-2.5 bg-slate-600 hover:bg-slate-700 text-white rounded-xl font-bold text-sm transition-colors flex items-center justify-center gap-2"
                >
                  <RotateCcw size={14} /> 처음부터 다시
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 오개념 요약 */}
      <div className="mt-5 card border border-slate-200 bg-slate-50">
        <p className="text-xs font-bold text-slate-600 mb-3 flex items-center gap-1">
          <AlertTriangle size={12} className="text-orange-500" /> 입체도형 주요 오개념 요약
        </p>
        <div className="space-y-2">
          {[
            { code: 'GP01', title: '각기둥·각뿔 공식 혼동', desc: '각뿔: 면n+1, 꼭짓점n+1, 모서리2n (기둥과 다름!)' },
            { code: 'GP02', title: '회전체 단면 방향 혼동', desc: '수직단면=원, 평행단면=직사각형(기둥)/이등변삼각형(뿔)' },
            { code: 'GP03', title: '원뿔 겉넓이: 옆면 공식 오류', desc: '원뿔 옆면=πrl (부채꼴), 원기둥 옆면=2πrh (직사각형)' },
            { code: 'GP04', title: '뿔 부피에서 (1/3) 누락', desc: '원뿔·각뿔은 반드시 (1/3)×밑면×높이. 기둥의 1/3!' },
          ].map(item => (
            <div key={item.code} className="flex items-start gap-2 p-2 bg-white rounded-lg border border-slate-100">
              <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-mono font-bold flex-shrink-0">
                {item.code}
              </span>
              <div>
                <p className="text-xs font-semibold text-slate-700">{item.title}</p>
                <p className="text-xs text-slate-500">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
